import { getTranslations } from "next-intl/server";
import Link from "next/link";
import { publicStorePath } from "@/lib/storefront/public-url";

interface PeptideInfoSectionProps {
	locale: string;
	channel: string;
}

export async function PeptideInfoSection({ locale }: PeptideInfoSectionProps) {
	const t = await getTranslations({ locale, namespace: "kitWebsite" });
	const questions = ["contents", "strength", "price", "mix", "image"] as const;
	return (
		<div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
			<section className="mb-10 grid gap-8 md:grid-cols-2" aria-labelledby="catalog-discovery-heading">
				<div>
					<h2 id="catalog-discovery-heading" className="text-xl font-semibold tracking-tight">
						{t("discovery.catalogTitle")}
					</h2>
					<p className="mt-3 text-sm leading-relaxed text-muted-foreground">
						{t.rich("discovery.catalogBody", {
							weight: (chunks) => (
								<Link
									href={publicStorePath(locale, "/categories/weight-management")}
									className="text-foreground underline underline-offset-4"
								>
									{chunks}
								</Link>
							),
							journal: (chunks) => (
								<Link
									href={publicStorePath(locale, "/journal")}
									className="text-foreground underline underline-offset-4"
								>
									{chunks}
								</Link>
							),
						})}
					</p>
				</div>
				<div>
					<h2 className="text-xl font-semibold tracking-tight">{t("discovery.selectionTitle")}</h2>
					<p className="mt-3 text-sm leading-relaxed text-muted-foreground">
						{t.rich("discovery.selectionBody", {
							faq: (chunks) => (
								<Link
									href={publicStorePath(locale, "/faq")}
									className="text-foreground underline underline-offset-4"
								>
									{chunks}
								</Link>
							),
							shipping: (chunks) => (
								<Link
									href={publicStorePath(locale, "/shipping")}
									className="text-foreground underline underline-offset-4"
								>
									{chunks}
								</Link>
							),
							contact: (chunks) => (
								<Link
									href={publicStorePath(locale, "/contact")}
									className="text-foreground underline underline-offset-4"
								>
									{chunks}
								</Link>
							),
						})}
					</p>
				</div>
			</section>
			<section
				className="bg-muted/40 rounded-2xl border border-border p-6 lg:p-8"
				aria-labelledby="peptide-info-heading"
			>
				<h2 id="peptide-info-heading" className="text-2xl font-semibold tracking-tight">
					{t("faq.title")}
				</h2>
				<p className="mt-3 text-sm text-muted-foreground">{t("faq.description")}</p>
				<div className="mt-6 divide-y divide-border">
					{questions.map((key) => (
						<details key={key} className="py-4">
							<summary className="cursor-pointer font-medium">{t(`faq.${key}Question`)}</summary>
							<p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
								{t(`faq.${key}Answer`)}
							</p>
						</details>
					))}
				</div>
			</section>
		</div>
	);
}
